import { type Interceptor } from '@connectrpc/connect';
import { settingsQueryKey } from '@/api/query-keys';
import { type SettingsDto } from '@/api/services/settings';
import { ctxKeyQueryClient } from '@/connect-query/context-keys';
import { missingQueryFnHandler } from '../missing-query-fn';

/** User-Agent prefix for Cloud UI Connect requests, matching go-client / qcloud-cli naming. */
export const CLOUD_UI_CLIENT = 'cloud-ui';

/** HTTP header forwarded by the API gateway as qc-user-agent audit metadata. */
export const QC_USER_AGENT_HEADER = 'Qc-User-Agent';

export function formatCloudUiUserAgent(version: string): string {
  return `${CLOUD_UI_CLIENT}/${version}`;
}

export function formatCloudUiQcUserAgent(version: string, browserUserAgent: string): string {
  const clientUserAgent = formatCloudUiUserAgent(version);
  return browserUserAgent ? `${clientUserAgent} ${browserUserAgent}` : clientUserAgent;
}

/**
 * User-Agent interceptor - identifies Cloud UI as the client on Connect requests.
 *
 * Browsers forbid scripts from setting the `User-Agent` header. Instead we send
 * `Qc-User-Agent`, which the API gateway forwards as `qc-user-agent` metadata.
 */
export const userAgentInterceptor: Interceptor = (next) => async (req) => {
  const queryClient = req.contextValues.get(ctxKeyQueryClient);
  const { tag } = await missingQueryFnHandler(queryClient.ensureQueryData<SettingsDto>({ queryKey: settingsQueryKey }));
  req.header.set(QC_USER_AGENT_HEADER, formatCloudUiQcUserAgent(tag, navigator.userAgent));
  return next(req);
};
